import type { Blocker, Transition } from "history";
import { Context, useCallback, useContext, useEffect, useState } from "react";
import { UNSAFE_NavigationContext, Navigator } from "react-router-dom";
import { ConfirmModalContent, Modal } from "../Modal";
import { useTranslation } from "react-i18next";

type BlockerNavigator = Navigator & {
  location: Location;
  block(blocker: Blocker): () => void;
};

type UnmountPromptProps = {
  hasUnsavedChanges: boolean;
  ignoredRoutes?: string[];
  onConfirm?: () => void;
};

const UnmountPrompt = ({
  hasUnsavedChanges,
  ignoredRoutes,
  onConfirm,
}: UnmountPromptProps) => {
  const { navigator } = useContext(
    UNSAFE_NavigationContext as Context<{
      basename: string;
      navigator: Navigator;
      static: boolean;
    }>
  );

  const [displayAlert, setDisplayAlert] = useState(false);
  const [transition, setTransition] = useState<Transition>();
  const { t } = useTranslation();

  // The handler for actually showing the prompt
  // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
  const handler = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
  };

  useEffect(() => {
    // If there are no changes, don't do anything and return empty callback
    // (stops & cleans up the useEffect)
    if (!hasUnsavedChanges) return () => null;

    // If there are changes, then set the `onbeforeunload`
    window.addEventListener("beforeunload", handler);
    // Clean it up, if the dirty state changes
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const {
      block,
      location: { pathname },
    } = navigator as BlockerNavigator;

    // Unblocks the transition
    const unblock = block((transition) => {
      const {
        location: { pathname: targetPathname },
      } = transition;

      const isPathIgnored = ignoredRoutes?.includes(targetPathname) || false;

      // We don't want to show the alert if the user is navigating to the same page.
      if (targetPathname === pathname) return;

      // If the path is ignored, then unblock the transition
      if (isPathIgnored) {
        unblock();
        transition.retry();
        return;
      }

      setDisplayAlert(true);

      setTransition({
        ...transition,
        retry() {
          unblock();
          transition.retry();
        },
      });
    });

    return unblock;
  }, [navigator, hasUnsavedChanges]);

  // Leave the page and transition to the next page
  const leavePage = useCallback(() => {
    transition?.retry();
    setDisplayAlert(false);
  }, [transition]);

  // Stay on the page and hide the alert
  const stayOnPage = useCallback(() => {
    setDisplayAlert(false);
  }, [setDisplayAlert]);

  return (
    <Modal
      srName="Unsaved Changes Detected"
      isOpen={displayAlert}
      onClose={stayOnPage}
      size={2}
      animate
    >
      <ConfirmModalContent
        heading={t("quotes:back_to_quote.unsaved_information") || ""}
        message={t("quotes:back_to_quote.message")}
        body={t("quotes:back_to_quote.body")}
        cancelMessage={t("quotes:back_to_quote.discard_button") || ""}
        confirmMessage={t("quotes:back_to_quote.confirm_button")}
        onCancel={() => leavePage()}
        onConfirm={() => {
          // If the user has specified the `onConfirm` prop, run that.
          if (onConfirm) {
            onConfirm();
            return;
          }
          // If they haven't, run the generic 'leave page' function
          leavePage();
        }}
      />
    </Modal>
  );
};

export default UnmountPrompt;
